/**
 * Ponte entre o descriptor e o formulário: inicializa o estado a partir da entidade
 * e monta o corpo da requisição com a coerção certa por tipo de campo.
 *
 * O estado do formulário é sempre string (ou boolean para `booleano`) — é o que os
 * inputs manipulam. A coerção para número/data/null acontece só na saída. Na
 * **edição** o corpo é um diff contra o valor carregado: só vai o que mudou, para
 * um PATCH não zerar por engano um campo que o usuário nem tocou.
 */
import type { DefCampo, GrupoCampos, RecursoBase } from '@/lib/recursos/tipos';
import type { ValorCampo } from '@/components/crud/CampoDinamico';
import { dinheiroParaNumero, numeroParaDinheiro } from '@/lib/mascaras';

export type Valores = Record<string, ValorCampo>;

/** Entidade (ou vazio) → estado inicial do formulário. */
export function valoresIniciais<T extends RecursoBase>(
  campos: DefCampo<T>[],
  item?: Partial<T>,
): Valores {
  const v: Valores = {};
  for (const c of campos) {
    const bruto = item?.[c.chave];
    if (c.tipo === 'booleano') {
      v[c.chave] = Boolean(bruto);
    } else if (bruto == null) {
      v[c.chave] = '';
    } else if (c.tipo === 'data') {
      v[c.chave] = String(bruto).slice(0, 10); // 'YYYY-MM-DD', sem hora
    } else if (c.tipo === 'dinheiro') {
      v[c.chave] = numeroParaDinheiro(bruto as string | number); // "3500.00" → "3.500,00"
    } else {
      v[c.chave] = String(bruto);
    }
  }
  return v;
}

/** Valor de formulário → valor de API, conforme o tipo. */
function coagir<T extends RecursoBase>(campo: DefCampo<T>, valor: ValorCampo): unknown {
  if (campo.tipo === 'booleano') return valor === true;
  const s = typeof valor === 'string' ? valor.trim() : '';
  if (s === '') return null;
  if (campo.tipo === 'numero') return Number(s);
  if (campo.tipo === 'dinheiro') return dinheiroParaNumero(s) || null; // "3.500,00" → "3500.00"
  return s;
}

/** Corpo de criação: só os campos preenchidos (o backend deduz/deduplica o resto). */
export function corpoCriar<T extends RecursoBase>(campos: DefCampo<T>[], valores: Valores): Record<string, unknown> {
  const corpo: Record<string, unknown> = {};
  for (const c of campos) {
    const v = coagir(c, valores[c.chave]);
    if (v !== null && v !== '') corpo[c.chave] = v;
  }
  return corpo;
}

/** Corpo de edição: diff contra o original. Campos `leitura` nunca entram. */
export function corpoEdicao<T extends RecursoBase>(
  grupos: GrupoCampos<T>[],
  valores: Valores,
  original: Valores,
): Record<string, unknown> {
  const corpo: Record<string, unknown> = {};
  for (const g of grupos) {
    for (const c of g.campos) {
      if (c.tipo === 'leitura') continue;
      if (valores[c.chave] !== original[c.chave]) {
        corpo[c.chave] = coagir(c, valores[c.chave]);
      }
    }
  }
  return corpo;
}

export const todosOsCampos = <T extends RecursoBase>(grupos: GrupoCampos<T>[]): DefCampo<T>[] =>
  grupos.flatMap((g) => g.campos);
